'use client';

import { useFocusRing } from '@react-aria/focus';

import { FormFieldSlot } from '../../internal/components/form/FormFieldSlot';
import { filterProps } from '../../internal/utils/filterProps';
import { focusRingStyles } from '../../styles/focusRing';
import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { CoreIcon } from '../core-icon/CoreIcon';
import { useTextFieldInternalContext } from './TextFieldContext';

const focusStyles = focusRingStyles();

const textFieldActionViewClasses = {
  root: 'zds-text-field-action-view',
} as const;

const actionViewStyles = tv({
  slots: {
    root: [
      textFieldActionViewClasses.root,
      'justify-center border-none bg-transparent p-0 font-[inherit]',
    ],
    divider: '',
  },
  variants: {
    isDisabled: {
      true: {},
      false: { root: 'cursor-pointer' },
    },
  },
  defaultVariants: { isDisabled: false },
});

interface TextFieldActionViewBaseProps {
  /** Accessible label for the toggle button (required for i18n). */
  label: string;
  /** Show a divider on the inner side (closest to input). */
  showDivider?: boolean;
}

type TextFieldActionViewProps = ComponentProps<
  TextFieldActionViewBaseProps,
  'button'
>;

type TextFieldActionViewComponent = Component<
  TextFieldActionViewBaseProps,
  'button'
>;

const TextFieldActionView: TextFieldActionViewComponent = (props) => {
  const [localProps, focusRingRaProps] = filterProps(props, ['useFocusRing']);

  const { label, showDivider, onClick, className, ref, ...restLocalProps } =
    localProps;

  const { inputId, type, setType, isDisabled } = useTextFieldInternalContext();
  const { isFocusVisible, focusProps } = useFocusRing(focusRingRaProps);
  const isVisible = type !== 'password';
  const styles = actionViewStyles({ isDisabled });

  return (
    <FormFieldSlot
      {...mergeProps(restLocalProps, focusProps)}
      component="button"
      ref={ref}
      type="button"
      showDivider={showDivider}
      slotProps={{ divider: { className: styles.divider() } }}
      disabled={isDisabled || undefined}
      onClick={(e) => {
        setType(isVisible ? 'password' : 'text');
        onClick?.(e);
      }}
      aria-label={label}
      aria-controls={inputId || undefined}
      aria-pressed={isVisible}
      data-focus-visible={isFocusVisible || undefined}
      className={focusStyles.root({ className: styles.root({ className }) })}
    >
      <CoreIcon name={isVisible ? 'viewoff' : 'view'} size="sm" />
    </FormFieldSlot>
  );
};

TextFieldActionView.displayName = 'TextField.ActionView';

export {
  TextFieldActionView,
  type TextFieldActionViewProps,
  type TextFieldActionViewBaseProps,
  textFieldActionViewClasses,
};
